import { useCallback, useEffect, useRef, useState } from 'react';
import { RATE_LIMITED_EVENT, type RateLimitedDetail } from '@/core/rateLimit';
import { t } from '../../i18n/t';

/**
 * Round 108, C4 — the one place a rate-limited learner is told what happened.
 *
 * Mounted once, at the app root (App.tsx), listening for the window event
 * apiProxy raises for every RATE_LIMITED 429 (core/rateLimit.ts). A banner at
 * the top of the screen rather than a per-screen message because most
 * throttled requests are fire-and-forget (the screen swallows them) — without
 * this the learner would see a half-loaded page with no explanation. It is
 * non-blocking (nothing is covered, nothing is cleared), counts down the
 * server's own Retry-After, says plainly when they can try again, and can be
 * dismissed. role="alert" so assistive tech announces it.
 */
const READY_VISIBLE_MS = 4000;

export default function RateLimitNotice() {
  // null = hidden; otherwise whole seconds left (0 = "you can try again now").
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const endsAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (hideRef.current) { clearTimeout(hideRef.current); hideRef.current = null; }
  }, []);

  const tick = useCallback(() => {
    const left = Math.max(0, Math.ceil((endsAtRef.current - Date.now()) / 1000));
    setSecondsLeft(left);
    if (left === 0) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      hideRef.current = setTimeout(() => setSecondsLeft(null), READY_VISIBLE_MS);
    }
  }, []);

  useEffect(() => {
    function onLimited(e: Event) {
      const seconds = (e as CustomEvent<RateLimitedDetail>).detail?.retryAfterSeconds ?? 10;
      // Several throttled requests arrive together: keep the LATEST end time,
      // never shorten a countdown already running.
      endsAtRef.current = Math.max(endsAtRef.current, Date.now() + seconds * 1000);
      clearTimers();
      tick();
      timerRef.current = setInterval(tick, 1000);
    }
    window.addEventListener(RATE_LIMITED_EVENT, onLimited);
    return () => { window.removeEventListener(RATE_LIMITED_EVENT, onLimited); clearTimers(); };
  }, [clearTimers, tick]);

  if (secondsLeft === null) return null;

  const ready = secondsLeft === 0;
  return (
    <div
      role="alert"
      data-testid="rate-limit-notice"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
        background: ready ? '#166534' : '#b45309', color: 'white',
        padding: '10px 16px', fontSize: '14px', textAlign: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
      }}
    >
      <span>{ready ? t('errors:rateLimit.ready') : t('errors:rateLimit.notice', { seconds: secondsLeft })}</span>
      <button
        type="button"
        onClick={() => { clearTimers(); endsAtRef.current = 0; setSecondsLeft(null); }}
        style={{
          background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.6)',
          borderRadius: '6px', padding: '2px 10px', cursor: 'pointer', fontSize: '13px',
        }}
      >{t('errors:rateLimit.dismiss')}</button>
    </div>
  );
}
